/**
 * Os links que o servidor manda por e-mail (docs/spec/email.md): a confirmação do e-mail (a conta nova
 * só usa o RPG Desk depois de abrir o link), a recuperação da senha e a troca do e-mail. Os prazos e os
 * limites ficam aqui porque as telas também os mostram.
 */

export const EMAIL_CONFIRMATION_LIMITS = {
  /** quanto tempo o link vale */
  linkMs: 48 * 60 * 60 * 1000,
  /** a espera entre um envio e o próximo (reenviar ou corrigir o e-mail) */
  resendMs: 60 * 1000,
  /** quantos envios uma conta faz por hora */
  sendsPerHour: 5,
  /** a conta que não confirma neste prazo, contado do cadastro, é apagada */
  deleteAfterMs: 7 * 24 * 60 * 60 * 1000,
  /** quantas contas um mesmo IP cria por hora, com a confirmação ligada */
  registrationsPerHour: 10,
} as const;

/** O parâmetro do endereço que leva o código do link: `https://rpgdesk.com/?confirm-email=<código>`. */
export const EMAIL_CONFIRMATION_PARAM = 'confirm-email';

/** As línguas dos e-mails, as mesmas da interface: o cadastro diz a da tela. */
export const EMAIL_LANGUAGES = ['pt-BR', 'en'] as const;
export type EmailLanguage = (typeof EMAIL_LANGUAGES)[number];

/**
 * O que o entrar, o cadastro e o /api/me dizem da conta que espera a confirmação (null: a conta já
 * confirmou, ou a confirmação está desligada no servidor).
 */
export type EmailConfirmationView = {
  /** o endereço para onde o link foi */
  email: string;
  /** o último envio que deu certo; null quando o último falhou (a tela pede para reenviar) */
  sentAt: number | null;
  /** quando o link do último envio vence */
  expiresAt: number;
  /** a partir de quando dá para pedir outro envio */
  resendAt: number;
  /** quando a conta é apagada, se não confirmar */
  deleteAt: number;
};

/** A recuperação da senha ("Esqueci a senha"): o link e os limites dos pedidos. */
export const PASSWORD_RESET_LIMITS = {
  /** quanto tempo o link vale */
  linkMs: 60 * 60 * 1000,
  /** a espera entre dois e-mails da recuperação para a mesma conta */
  resendMs: 60 * 1000,
  /** quantos e-mails da recuperação uma conta recebe por hora */
  sendsPerHour: 5,
  /** quantos pedidos um mesmo IP faz por hora */
  requestsPerHour: 10,
} as const;

/** O parâmetro do endereço que leva o código da recuperação: `https://rpgdesk.com/?reset-password=<código>`. */
export const PASSWORD_RESET_PARAM = 'reset-password';

/**
 * A troca do e-mail de uma conta já confirmada, esperando o link que foi para o endereço novo. O link é
 * o da confirmação (`?confirm-email=`), com os mesmos prazos (EMAIL_CONFIRMATION_LIMITS).
 */
export type EmailChangeView = {
  /** o endereço novo, para onde o link foi */
  email: string;
  /** o último envio que deu certo; null quando o último falhou */
  sentAt: number | null;
  /** quando o link do último envio vence */
  expiresAt: number;
  /** a partir de quando dá para pedir outro envio */
  resendAt: number;
};

/** O e-mail da conta, como as Configurações mostram (GET /api/me/email). */
export type AccountEmailView = {
  /** o e-mail da conta; null na conta que se cadastrou sem ele */
  email: string | null;
  /** a troca que espera o link; null sem troca */
  change: EmailChangeView | null;
  /** o servidor manda os links (tem RPGDESK_APP_URL); sem isso, não dá para trocar o e-mail */
  available: boolean;
};
